import jsPDF from 'jspdf'
import React from 'react'
import { renderToStaticMarkup } from 'react-dom/server'
import { FiHash, FiPhone, FiMapPin, FiTool, FiUsers, FiCheckCircle, FiCalendar, FiClock, FiActivity, FiZap } from 'react-icons/fi'
import { ReactComponent as GeneratorIcon } from '../shared/img/icon/generator.svg'
import api from './api'
import logo from '../shared/img/logo.png'
import { formatCPF, formatCNPJ, formatCEP, formatPhone } from './formatters'
import { buildChecklistSections } from './checklistSections'
import { getOrderServiceAddress } from './generatorLocation'

// Layout desenhado manualmente com jsPDF, espelhando o protótipo HTML/CSS que
// foi validado em ServiceOrderPDFPreview.js (frontend/src/pages/ServiceOrder/ServiceOrderConcluded).
// jsPDF não tem grid/flexbox, então o "grid" de cartões é recalculado na mão
// (renderCardGrid) — o resultado é próximo do protótipo, mas não pixel a pixel.
// Os ícones (react-icons) viram PNG em tempo de execução: renderiza o SVG do
// componente pra string, desenha num canvas escondido e lê o PNG de lá — jsPDF
// não sabe desenhar SVG/React diretamente.

const ORANGE = [240, 120, 32]
const ORANGE_DARK = [212, 100, 16]
const ORANGE_LIGHT_BORDER = [252, 217, 179]
const CARD_BG = [249, 250, 251]
const CARD_BORDER = [240, 240, 240]
const LABEL_GRAY = [156, 163, 175]
const DARK_TEXT = [31, 41, 55]
const MUTED_TEXT = [107, 114, 128]
const GREEN_BG = [220, 252, 231]
const GREEN_TEXT = [22, 163, 74]
const RED_BG = [254, 226, 226]
const RED_TEXT = [220, 38, 38]
const PILL_NEUTRAL_BG = [229, 231, 235]

const PAGE_WIDTH = 210
const PAGE_BOTTOM = 280
const MARGIN = 14
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2

function formatDate(value) {
    if (!value) return ''
    const [year, month, day] = value.split('-')
    return day && month && year ? `${day}/${month}/${year}` : value
}

function formatDuration(totalSeconds) {
    const h = Math.floor(totalSeconds / 3600)
    const m = Math.floor((totalSeconds % 3600) / 60)
    if (h === 0) return `${m}min`
    return `${h}h ${m}min`
}

function formatDateTime(value) {
    if (!value) return null
    const d = new Date(value)
    if (isNaN(d.getTime())) return null
    const date = d.toLocaleDateString('pt-BR')
    const time = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    return `${date} ${time}`
}

function formatDateOnly(value) {
    if (!value) return null
    const d = new Date(value)
    if (isNaN(d.getTime())) return null
    return d.toLocaleDateString('pt-BR')
}

// Monta nome/documento/endereço do cliente a partir do snapshot salvo na OS —
// mesma lógica de ServiceOrderPDFPreview.js (getClientInfo).
function getClientInfo(client, addr) {
    if (!client) return null

    const name = client.personType === 'juridica'
        ? (client.corporateName || client.fantasyName)
        : (client.completeName || client.name)
    const document = client.personType === 'juridica' ? formatCNPJ(client.CNPJ) : formatCPF(client.CPF)
    const documentLabel = client.personType === 'juridica' ? 'CNPJ' : 'CPF'
    // endereço = local do serviço (do gerador ou, se for o mesmo, do cliente), congelado na OS
    const addressLine = [addr.logradouro, addr.number].filter(Boolean).join(', ')

    return {
        name: name || client.label || client.name || '—',
        code: client.code,
        document,
        documentLabel,
        addressLine,
        complement: addr.complement,
        district: addr.district,
        city: addr.city,
        state: addr.state,
        zipCode: formatCEP(addr.zipCode),
        phone: formatPhone(client.phone),
    }
}

function fetchDataUrl(url, auth) {
    const request = auth
        ? api.get(url, {
            responseType: 'blob',
            headers: (() => {
                const token = localStorage.getItem('token')
                return token ? { Authorization: `Bearer ${JSON.parse(token)}` } : {}
            })(),
        }).then((response) => response.data)
        : fetch(url).then((response) => response.blob())

    return request.then((blob) => new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = () => resolve(reader.result)
        reader.onerror = reject
        reader.readAsDataURL(blob)
    }))
}

function getImageSize(dataUrl) {
    return new Promise((resolve) => {
        const img = new Image()
        img.onload = () => resolve({ width: img.naturalWidth || 1, height: img.naturalHeight || 1 })
        img.onerror = () => resolve({ width: 1, height: 1 })
        img.src = dataUrl
    })
}

// Renderiza um componente de ícone React (react-icons ou SVG customizado) num
// PNG de alta resolução (fixo em 64x64, o ícone sempre é desenhado pequeno no
// PDF, então não precisa escalar por elemento).
// `color` funciona pros ícones do react-icons (usam fill="currentColor" internamente).
// O SVG customizado do gerador tem fill literal em cada path/polygon (mesma razão
// pela qual o CSS do app tem que sobrescrever com `path, polygon { fill: ... }`),
// então pra ele usamos `forceFill`, que troca todo fill="..." (exceto "none") no
// markup antes de rasterizar.
function iconToPngDataUrl(IconComponent, { color, forceFill } = {}) {
    let svgMarkup = renderToStaticMarkup(React.createElement(IconComponent, { size: 64, color }))
    if (forceFill) {
        svgMarkup = svgMarkup.replace(/fill="(?!none)[^"]*"/g, `fill="${forceFill}"`)
    }
    const svgUrl = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgMarkup)))}`

    return new Promise((resolve, reject) => {
        const img = new Image()
        img.onload = () => {
            const canvas = document.createElement('canvas')
            const size = 64
            canvas.width = size
            canvas.height = size
            const ctx = canvas.getContext('2d')
            // Preserva a proporção original do SVG (o do gerador não é quadrado) em
            // vez de esticar pra caber no canvas — sem isso o ícone fica "amassado".
            const naturalWidth = img.naturalWidth || size
            const naturalHeight = img.naturalHeight || size
            const scale = Math.min(size / naturalWidth, size / naturalHeight)
            const drawWidth = naturalWidth * scale
            const drawHeight = naturalHeight * scale
            const offsetX = (size - drawWidth) / 2
            const offsetY = (size - drawHeight) / 2
            ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight)
            resolve(canvas.toDataURL('image/png'))
        }
        img.onerror = reject
        img.src = svgUrl
    })
}

const ICON_GRAY = '#9ca3af'

const ICON_DEFS = {
    hash: [FiHash, { color: ICON_GRAY }],
    phone: [FiPhone, { color: ICON_GRAY }],
    mapPin: [FiMapPin, { color: ICON_GRAY }],
    tool: [FiTool, { color: ICON_GRAY }],
    users: [FiUsers, { color: ICON_GRAY }],
    checkCircle: [FiCheckCircle, { color: ICON_GRAY }],
    calendar: [FiCalendar, { color: ICON_GRAY }],
    clock: [FiClock, { color: ICON_GRAY }],
    activity: [FiActivity, { color: ICON_GRAY }],
    zap: [FiZap, { color: ICON_GRAY }],
    generator: [GeneratorIcon, { forceFill: ICON_GRAY }],
}

async function loadIcons() {
    const entries = await Promise.all(
        Object.entries(ICON_DEFS).map(async ([key, [Component, options]]) => {
            try {
                const dataUrl = await iconToPngDataUrl(Component, options)
                return [key, dataUrl]
            } catch (error) {
                return [key, null]
            }
        })
    )
    return Object.fromEntries(entries)
}

function imageFormat(dataUrl) {
    return dataUrl.startsWith('data:image/png') ? 'PNG' : 'JPEG'
}

// Monta o documento e devolve a instância do jsPDF, sem decidir o que fazer
// com ele — quem chamar decide entre baixar ou abrir em outra aba.
export async function buildServiceOrderPDF(serviceOrder) {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' })
    const icons = await loadIcons()
    let y = MARGIN

    function ensureSpace(needed) {
        if (y + needed > PAGE_BOTTOM) {
            doc.addPage()
            y = MARGIN
        }
    }

    // ── Cartões (grid) ──────────────────────────────────────────────────
    // Layout tipo CSS grid de 3 colunas, com suporte a cartões "wide" (2 colunas).
    function renderCardGrid(cards, startY) {
        const columns = 3
        const gap = 3
        const colWidth = (CONTENT_WIDTH - gap * (columns - 1)) / columns
        const padX = 3

        const rows = []
        let currentRow = []
        let col = 0
        cards.forEach((card) => {
            const span = card.wide ? 2 : 1
            if (col + span > columns) {
                rows.push(currentRow)
                currentRow = []
                col = 0
            }
            currentRow.push({ ...card, colStart: col, span })
            col += span
        })
        if (currentRow.length) rows.push(currentRow)

        let cursorY = startY
        rows.forEach((row) => {
            doc.setFontSize(9)
            doc.setFont(undefined, 'normal')
            let maxLines = 1
            const rendered = row.map(({ label, value, colStart, span, icon }) => {
                const cardWidth = colWidth * span + gap * (span - 1)
                const lines = doc.splitTextToSize(value || '—', cardWidth - padX * 2)
                maxLines = Math.max(maxLines, lines.length)
                return { label, colStart, span, cardWidth, lines, icon }
            })
            const cardHeight = 5 + maxLines * 3.6 + 3

            if (cursorY + cardHeight > PAGE_BOTTOM) {
                doc.addPage()
                cursorY = MARGIN
            }

            rendered.forEach(({ label, colStart, span, cardWidth, lines, icon }) => {
                const cardX = MARGIN + colStart * (colWidth + gap)

                doc.setDrawColor(...CARD_BORDER)
                doc.setFillColor(...CARD_BG)
                doc.setLineWidth(0.2)
                doc.roundedRect(cardX, cursorY, cardWidth, cardHeight, 1.2, 1.2, 'FD')

                // Label é 6.4pt caixa-alta — a faixa de cap-height (a "altura visual"
                // das letras maiúsculas) fica entre a base do texto e ~1.58mm acima
                // dela. Centralizamos o ícone nessa faixa em vez de alinhar pelo topo.
                const iconSize = 2
                const labelBaselineY = cursorY + 4
                const capHeight = 1.58
                const iconY = labelBaselineY - capHeight / 2 - iconSize / 2
                const labelX = icon ? cardX + padX + iconSize + 1 : cardX + padX
                if (icon) {
                    try {
                        doc.addImage(icon, 'PNG', cardX + padX, iconY, iconSize, iconSize)
                    } catch (error) {
                        // se o ícone falhar, o cartão continua sem ele
                    }
                }

                doc.setFontSize(6.4)
                doc.setFont(undefined, 'bold')
                doc.setTextColor(...LABEL_GRAY)
                doc.text(label.toUpperCase(), labelX, cursorY + 4)

                doc.setFontSize(9)
                doc.setFont(undefined, 'normal')
                doc.setTextColor(...DARK_TEXT)
                doc.text(lines, cardX + padX, cursorY + 8.2)
            })

            cursorY += cardHeight + gap
        })

        return cursorY
    }

    function sectionTitle(text) {
        ensureSpace(12)
        doc.setFontSize(10)
        doc.setFont(undefined, 'bold')
        doc.setTextColor(...ORANGE_DARK)
        doc.text(text.toUpperCase(), MARGIN, y)
        y += 1.5
        doc.setDrawColor(...ORANGE_LIGHT_BORDER)
        doc.setLineWidth(0.6)
        doc.line(MARGIN, y, PAGE_WIDTH - MARGIN, y)
        y += 6
    }

    // ── Cabeçalho ────────────────────────────────────────────────────────
    try {
        const logoDataUrl = await fetchDataUrl(logo, false)
        const { width, height } = await getImageSize(logoDataUrl)
        const logoHeight = 12
        const logoWidth = (width / height) * logoHeight
        doc.addImage(logoDataUrl, imageFormat(logoDataUrl), MARGIN, y, logoWidth, logoHeight)
    } catch (error) {
        // sem logo disponível, segue sem travar o documento
    }

    doc.setFontSize(8)
    doc.setFont(undefined, 'bold')
    doc.setTextColor(...LABEL_GRAY)
    doc.text('ORDEM DE SERVIÇO', PAGE_WIDTH - MARGIN, y + 4, { align: 'right' })
    doc.setFontSize(13)
    doc.setFont(undefined, 'bold')
    doc.setTextColor(...DARK_TEXT)
    doc.text(`#${serviceOrder.code || '—'}`, PAGE_WIDTH - MARGIN, y + 10, { align: 'right' })

    y += 16
    doc.setDrawColor(...ORANGE)
    doc.setLineWidth(1)
    doc.line(MARGIN, y, PAGE_WIDTH - MARGIN, y)
    y += 8

    // ── Cliente ──────────────────────────────────────────────────────────
    const clientInfo = getClientInfo(serviceOrder.client, getOrderServiceAddress(serviceOrder))
    if (clientInfo) {
        doc.setFontSize(10.5)
        doc.setFont(undefined, 'bold')
        doc.setTextColor(...DARK_TEXT)
        doc.text(`Nome/Razão Social: ${clientInfo.name}`, MARGIN, y)
        y += 5

        y = renderCardGrid([
            { label: 'Código', value: clientInfo.code, icon: icons.hash },
            { label: clientInfo.documentLabel, value: clientInfo.document, icon: icons.hash },
            { label: 'Telefone', value: clientInfo.phone, icon: icons.phone },
        ], y)
        y += 4

        // Local do serviço: onde o gerador está e o atendimento foi executado
        ensureSpace(14)
        doc.setFontSize(10.5)
        doc.setFont(undefined, 'bold')
        doc.setTextColor(...DARK_TEXT)
        doc.text('Local do serviço', MARGIN, y)
        y += 5

        y = renderCardGrid([
            { label: 'Endereço', value: clientInfo.addressLine, icon: icons.mapPin },
            { label: 'Complemento', value: clientInfo.complement, icon: icons.mapPin },
            { label: 'Bairro', value: clientInfo.district, icon: icons.mapPin },
            { label: 'Cidade', value: clientInfo.city, icon: icons.mapPin },
            { label: 'UF', value: clientInfo.state, icon: icons.mapPin },
            { label: 'CEP', value: clientInfo.zipCode, icon: icons.mapPin },
        ], y)
        y += 6
    }

    // ── Detalhes do atendimento ─────────────────────────────────────────
    const scheduled = serviceOrder.scheduledDate ? formatDate(serviceOrder.scheduledDate) : null
    const requestedAt = formatDateOnly(serviceOrder.createdAt)
    const executedAt = formatDateTime(serviceOrder.filledAt)
    const startedAt = (serviceOrder.filledAt && typeof serviceOrder.timeSpentSeconds === 'number')
        ? formatDateTime(new Date(serviceOrder.filledAt).getTime() - serviceOrder.timeSpentSeconds * 1000)
        : null
    const duration = typeof serviceOrder.timeSpentSeconds === 'number'
        ? formatDuration(serviceOrder.timeSpentSeconds)
        : null
    const technicianNames = (serviceOrder.technicians || []).map(t => t.name || t.label).join(', ')

    sectionTitle('Detalhes do atendimento')
    y = renderCardGrid([
        { label: 'Tipo de OS', value: serviceOrder.procedure?.name || serviceOrder.procedure?.label, icon: icons.tool },
        { label: 'Equipe responsável', value: technicianNames, wide: true, icon: icons.users },
        { label: 'Status', value: serviceOrder.status?.label, icon: icons.checkCircle },
        { label: 'Data de solicitação', value: requestedAt, icon: icons.calendar },
        { label: 'Agendado', value: scheduled, icon: icons.calendar },
        { label: 'Início', value: startedAt, icon: icons.clock },
        { label: 'Término', value: executedAt, icon: icons.clock },
        { label: 'Duração', value: duration, icon: icons.clock },
    ], y)
    y += 6

    // ── Gerador ──────────────────────────────────────────────────────────
    sectionTitle('Gerador')
    y = renderCardGrid([
        { label: 'Nome', value: serviceOrder.generator?.label || serviceOrder.generator?.name, icon: icons.generator },
        { label: 'Número de Série', value: serviceOrder.generator?.serialNumber, icon: icons.hash },
        { label: 'Nº de Patrimônio', value: serviceOrder.generator?.patrimonyNumber, icon: icons.hash },
        { label: 'Fabricante', value: serviceOrder.generator?.manufacturer, icon: icons.tool },
        { label: 'Potência', value: serviceOrder.generator?.power, icon: icons.activity },
        { label: 'Alternador', value: serviceOrder.generator?.alternator, icon: icons.zap },
    ], y)

    if (serviceOrder.generator?.description) {
        y += 3
        doc.setFontSize(9)
        doc.setFont(undefined, 'normal')
        const lines = doc.splitTextToSize(serviceOrder.generator.description, CONTENT_WIDTH - 10)
        const boxHeight = lines.length * 4.2 + 6
        ensureSpace(boxHeight)
        doc.setFillColor(...CARD_BG)
        doc.rect(MARGIN, y, CONTENT_WIDTH, boxHeight, 'F')
        doc.setDrawColor(...ORANGE)
        doc.setLineWidth(0.9)
        doc.line(MARGIN, y, MARGIN, y + boxHeight)
        doc.setTextColor(...MUTED_TEXT)
        doc.text(lines, MARGIN + 5, y + 5.5)
        y += boxHeight
    }
    y += 6

    // ── Observação do técnico ───────────────────────────────────────────
    if (serviceOrder.observation) {
        sectionTitle('Observação do técnico')
        doc.setFontSize(9)
        doc.setFont(undefined, 'normal')
        const lines = doc.splitTextToSize(serviceOrder.observation, CONTENT_WIDTH - 10)
        const boxHeight = lines.length * 4.2 + 6
        ensureSpace(boxHeight)
        doc.setFillColor(...CARD_BG)
        doc.rect(MARGIN, y, CONTENT_WIDTH, boxHeight, 'F')
        doc.setTextColor(...MUTED_TEXT)
        doc.text(lines, MARGIN + 5, y + 5.5)
        y += boxHeight + 6
    }

    // ── Anexos ───────────────────────────────────────────────────────────
    const attachments = serviceOrder.attachments || []
    if (attachments.length > 0) {
        sectionTitle('Anexos')

        const columns = 3
        const gap = 4
        const colWidth = (CONTENT_WIDTH - gap * (columns - 1)) / columns
        const boxHeight = colWidth * 0.75 // proporção 4:3

        for (let i = 0; i < attachments.length; i += columns) {
            const rowPhotos = attachments.slice(i, i + columns)

            doc.setFontSize(7.5)
            doc.setFont(undefined, 'normal')
            const prepared = []
            let maxDescLines = 0
            for (const photo of rowPhotos) {
                const descLines = photo.description ? doc.splitTextToSize(photo.description, colWidth) : []
                maxDescLines = Math.max(maxDescLines, descLines.length)
                prepared.push({ photo, descLines })
            }
            const rowHeight = boxHeight + (maxDescLines > 0 ? maxDescLines * 3.2 + 2 : 0)
            ensureSpace(rowHeight + gap)

            for (let colIndex = 0; colIndex < prepared.length; colIndex++) {
                const { photo, descLines } = prepared[colIndex]
                const cellX = MARGIN + colIndex * (colWidth + gap)

                // fundo do quadro (letterbox) + borda
                doc.setFillColor(243, 244, 246)
                doc.setDrawColor(...CARD_BORDER)
                doc.setLineWidth(0.2)
                doc.roundedRect(cellX, y, colWidth, boxHeight, 1.5, 1.5, 'FD')

                try {
                    const dataUrl = await fetchDataUrl(`/images/serviceOrder/${photo.filename}`, true)
                    const { width, height } = await getImageSize(dataUrl)
                    const scale = Math.min(colWidth / width, boxHeight / height)
                    const imgWidth = width * scale
                    const imgHeight = height * scale
                    const imgX = cellX + (colWidth - imgWidth) / 2
                    const imgY = y + (boxHeight - imgHeight) / 2
                    doc.addImage(dataUrl, imageFormat(dataUrl), imgX, imgY, imgWidth, imgHeight)
                } catch (error) {
                    // foto que falhar ao carregar só fica com o quadro vazio
                }

                if (descLines.length > 0) {
                    doc.setFontSize(7.5)
                    doc.setFont(undefined, 'normal')
                    doc.setTextColor(...MUTED_TEXT)
                    doc.text(descLines, cellX + colWidth / 2, y + boxHeight + 3.5, { align: 'center' })
                }
            }

            y += rowHeight + gap
        }
        y += 4
    }

    // ── Checklist ────────────────────────────────────────────────────────
    const checklist = serviceOrder.checklist || []
    if (checklist.length > 0) {
        const checklistSections = buildChecklistSections(checklist)

        sectionTitle('Checklist realizado')

        checklistSections.forEach((section, sectionIndex) => {
            ensureSpace(10)
            if (sectionIndex > 0) y += 4
            doc.setFontSize(7.5)
            doc.setFont(undefined, 'bold')
            doc.setTextColor(...MUTED_TEXT)
            doc.text(section.groupLabel.toUpperCase(), MARGIN, y)
            y += 3.5

            section.items.forEach((item) => {
                doc.setFontSize(9)
                doc.setFont(undefined, 'normal')
                const isYesNo = item.fillType?.value === 'yesno'
                const valueText = isYesNo
                    ? (item.response || '—')
                    : `${item.response || '—'}${item.fillType?.value === 'measurement' && item.unit ? ` ${item.unit}` : ''}`

                const nameMaxWidth = CONTENT_WIDTH - 45
                const nameLines = doc.splitTextToSize(item.name, nameMaxWidth)
                const rowHeight = Math.max(7, nameLines.length * 4 + 3)
                ensureSpace(rowHeight + 2)

                doc.setFillColor(250, 250, 250)
                doc.roundedRect(MARGIN, y, CONTENT_WIDTH, rowHeight, 1.2, 1.2, 'F')

                doc.setTextColor(...DARK_TEXT)
                doc.text(nameLines, MARGIN + 4, y + rowHeight / 2 - (nameLines.length - 1) * 2 + 1, { baseline: 'middle' })

                if (isYesNo) {
                    const pillBg = item.response === 'Sim' ? GREEN_BG : item.response === 'Não' ? RED_BG : PILL_NEUTRAL_BG
                    const pillText = item.response === 'Sim' ? GREEN_TEXT : item.response === 'Não' ? RED_TEXT : MUTED_TEXT
                    doc.setFontSize(8)
                    doc.setFont(undefined, 'bold')
                    const pillWidth = doc.getTextWidth(valueText) + 8
                    const pillX = MARGIN + CONTENT_WIDTH - 4 - pillWidth
                    const pillY = y + rowHeight / 2 - 3
                    doc.setFillColor(...pillBg)
                    doc.roundedRect(pillX, pillY, pillWidth, 6, 3, 3, 'F')
                    doc.setTextColor(...pillText)
                    doc.text(valueText, pillX + pillWidth / 2, pillY + 4, { align: 'center' })
                } else {
                    doc.setFontSize(9)
                    doc.setFont(undefined, 'bold')
                    doc.setTextColor(...DARK_TEXT)
                    doc.text(valueText, MARGIN + CONTENT_WIDTH - 4, y + rowHeight / 2 + 1.2, { align: 'right', baseline: 'middle' })
                }

                y += rowHeight + 2
            })
        })
        y += 4
    }

    // ── Assinatura ───────────────────────────────────────────────────────
    const signatureColumns = [
        { caption: 'Tipo Responsável', value: serviceOrder.signerRole },
        { caption: 'Nome', value: serviceOrder.signerName },
    ]
    const slotHeight = 14
    ensureSpace(slotHeight + 12)
    y += 6

    const sigGap = 8
    const sigColWidth = (CONTENT_WIDTH - sigGap * 2) / 3

    signatureColumns.forEach((col, index) => {
        const colX = MARGIN + index * (sigColWidth + sigGap)
        if (col.value) {
            doc.setFontSize(9)
            doc.setFont(undefined, 'normal')
            doc.setTextColor(...DARK_TEXT)
            doc.text(col.value, colX + sigColWidth / 2, y + slotHeight - 2, { align: 'center' })
        }
        doc.setDrawColor(156, 163, 175)
        doc.setLineWidth(0.3)
        doc.line(colX, y + slotHeight, colX + sigColWidth, y + slotHeight)
        doc.setFontSize(7)
        doc.setFont(undefined, 'bold')
        doc.setTextColor(...LABEL_GRAY)
        doc.text(col.caption.toUpperCase(), colX + sigColWidth / 2, y + slotHeight + 4, { align: 'center' })
    })

    // Coluna da assinatura digital
    const sigColX = MARGIN + 2 * (sigColWidth + sigGap)
    if (serviceOrder.signature) {
        try {
            const dataUrl = await fetchDataUrl(`/images/serviceOrder/${serviceOrder.signature}`, true)
            const { width, height } = await getImageSize(dataUrl)
            const scale = Math.min(sigColWidth / width, slotHeight / height)
            const imgWidth = width * scale
            const imgHeight = height * scale
            const imgX = sigColX + (sigColWidth - imgWidth) / 2
            const imgY = y + slotHeight - imgHeight
            doc.addImage(dataUrl, imageFormat(dataUrl), imgX, imgY, imgWidth, imgHeight)
        } catch (error) {
            // sem assinatura disponível, fica só a linha em branco
        }
    }
    doc.setDrawColor(156, 163, 175)
    doc.setLineWidth(0.3)
    doc.line(sigColX, y + slotHeight, sigColX + sigColWidth, y + slotHeight)
    doc.setFontSize(7)
    doc.setFont(undefined, 'bold')
    doc.setTextColor(...LABEL_GRAY)
    doc.text('ASSINATURA DIGITAL', sigColX + sigColWidth / 2, y + slotHeight + 4, { align: 'center' })

    return doc
}

export async function downloadServiceOrderPDF(serviceOrder) {
    const doc = await buildServiceOrderPDF(serviceOrder)
    doc.save(`OS-${serviceOrder.code || 'servico'}.pdf`)
}

// Abre o PDF numa aba nova sem forçar download. A aba precisa ser aberta de
// forma síncrona (antes dos awaits de buildServiceOrderPDF) pra não cair no
// bloqueio de pop-up do navegador; o conteúdo é preenchido depois, quando o
// PDF fica pronto.
export async function openServiceOrderPDF(serviceOrder, newTab) {
    try {
        const doc = await buildServiceOrderPDF(serviceOrder)
        const blobUrl = doc.output('bloburl')
        if (newTab) newTab.location.href = blobUrl
    } catch (error) {
        if (newTab) newTab.close()
        throw error
    }
}
